import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Plus, Trash2, AlertTriangle, Info, CheckCircle2, X,
  NotebookPen, ArrowLeft, Trophy, Clock, User, MapPin, Route, GraduationCap,
  ChevronDown, ChevronUp, Pencil, CalendarClock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppointmentContextSidebar from "@/components/autoescuela/AppointmentContextSidebar";
import QuickAddDialog, { QUICK_ADD_VALUE } from "@/components/autoescuela/QuickAddDialog";
import api, { formatApiError } from "@/lib/api";
import { addMinutes, studentShortLabel } from "@/lib/utils-app";
import StudentPicker from "@/components/autoescuela/StudentPicker";

const toMinutes = (v) => {
  const [h, m] = String(v || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : 0;
};

const CPS_OPTIONS = [
  { v: 0, label: "Automático (programa de estudio)" },
  { v: 1, label: "1 clase (excepción)" },
  { v: 2, label: "2 clases (90 min)" },
  { v: 3, label: "3 clases (135 min)" },
  { v: 4, label: "4 clases (180 min)" },
];

function formatDateES(iso) {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
  } catch { return iso; }
}

function ScoreBadge({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const cls =
    s >= 90 ? "bg-emerald-500 text-white" :
    s >= 75 ? "bg-emerald-100 text-emerald-800" :
    s >= 60 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${cls}`}>
      <Trophy className="h-3 w-3" /> {s}
    </span>
  );
}

const DEFAULT_WINDOWS = [{ start: "09:00", end: "12:00" }, { start: "16:30", end: "22:00" }];

const EMPTY_COMMON = {
  classes_to_plan: 4, classes_per_session: 0,
  plan_start_date: "", plan_end_date: "",
  weekend_enabled: false, weekend_days: [],
};

export default function KaiPlannerDialog({ open, onOpenChange, onChanged, teacher = "" }) {
  const [step, setStep] = useState("select"); // select | config | review | result
  const [common, setCommon] = useState(EMPTY_COMMON);
  const [timeWindows, setTimeWindows] = useState(DEFAULT_WINDOWS);
  const [notices, setNotices] = useState([]);
  const [students, setStudents] = useState([]);
  const [items, setItems] = useState([]);
  const [pickerValue, setPickerValue] = useState("");
  const [studentNotes, setStudentNotes] = useState({});
  const [validating, setValidating] = useState(false);
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [plans, setPlans] = useState([]);
  const [acting, setActing] = useState(false);
  const [contexts, setContexts] = useState({});
  const [expanded, setExpanded] = useState({});
  const [sugs, setSugs] = useState({});
  // V1.72 · El reparto de sesiones lo manda el backend (fuente única).
  const [splitHints, setSplitHints] = useState({});
  const [locations, setLocations] = useState([]);
  const [quickAdd, setQuickAdd] = useState(null); // {idx, field}
  const [editDraft, setEditDraft] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("select");
    setItems([]);
    setReports([]);
    setPlans([]);
    setNotices([]);
    setCommon(EMPTY_COMMON);
    setContexts({});
    setExpanded({});
    setSugs({});
    setStudentNotes({});
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get("/students"), api.get("/locations")]);
        setStudents((Array.isArray(s.data) ? s.data : []).filter((x) => x.active !== false));
        setLocations(Array.isArray(l.data) ? l.data : []);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
      try {
        const { data } = await api.get("/kai-planner/time-windows");
        setTimeWindows(Array.isArray(data?.windows) && data.windows.length ? data.windows : DEFAULT_WINDOWS);
      } catch { setTimeWindows(DEFAULT_WINDOWS); }
    })();
  }, [open]);

  const usedIds = useMemo(() => new Set(items.map((i) => i.student_id)), [items]);
  // V1.76 · Nombre + primer apellido + inicial del segundo. Si dos alumnos
  // comparten etiqueta (dos Danielas, varias Pilar), se añade DNI o teléfono.
  const labelById = useMemo(() => {
    const base = {};
    const seen = {};
    students.forEach((s) => {
      const l = studentShortLabel(s) || s.dni || s.email || "(sin nombre)";
      base[s.id] = l;
      seen[l] = (seen[l] || 0) + 1;
    });
    students.forEach((s) => {
      if (seen[base[s.id]] > 1) {
        const extra = s.dni || s.phone || "";
        if (extra) base[s.id] = `${base[s.id]} · ${extra}`;
      }
    });
    return base;
  }, [students]);
  const available = useMemo(
    () => students
      .filter((s) => !usedIds.has(s.id))
      .map((s) => ({ ...s, label: labelById[s.id] }))
      .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [students, usedIds, labelById],
  );
  const studentsById = useMemo(() => Object.fromEntries(students.map((s) => [s.id, s])), [students]);
  const labelOf = useCallback((sid) => labelById[sid] || "(sin nombre)", [labelById]);

  const addStudent = () => {
    if (!pickerValue) return;
    const sid = pickerValue;
    setItems((prev) => [...prev, {
      student_id: sid, days_window: 0, ...common,
      weekend_days: [...common.weekend_days],
      tentative_exam_date: "", pickup_location_id: "", dropoff_location_id: "",
    }]);
    setPickerValue("");
    api.get(`/students/${sid}/appointment-context`)
      .then(({ data }) => setContexts((prev) => ({ ...prev, [sid]: data })))
      .catch(() => {});
    api.get(`/kai-planner/student-notes/${sid}`)
      .then(({ data }) => setStudentNotes((prev) => ({ ...prev, [sid]: { text: data?.text || "", dirty: false, saving: false } })))
      .catch(() => {});
  };

  const setNoteText = (sid, text) =>
    setStudentNotes((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), text, dirty: true } }));

  const saveStudentNote = async (sid) => {
    const note = studentNotes[sid];
    if (!note) return;
    setStudentNotes((prev) => ({ ...prev, [sid]: { ...note, saving: true } }));
    try {
      await api.put(`/kai-planner/student-notes/${sid}`, { text: note.text });
      setStudentNotes((prev) => ({ ...prev, [sid]: { ...note, dirty: false, saving: false } }));
      toast.success("Nota guardada — Kai la aplicará a este alumno.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setStudentNotes((prev) => ({ ...prev, [sid]: { ...note, saving: false } }));
    }
  };

  const toggleFicha = (sid, it) => {
    const opening = !expanded[sid];
    setExpanded((prev) => ({ ...prev, [sid]: opening }));
    if (opening && !sugs[sid]) {
      setSugs((prev) => ({ ...prev, [sid]: { loading: true, list: [] } }));
      api.get("/appointments/suggest-slots", {
        params: {
          student_id: sid,
          teacher: contexts[sid]?.profesor_habitual || "",
          from_date: new Date().toISOString().slice(0, 10),
          duration_min: (it?.classes_per_session || 2) * 45,
          max_suggestions: 6,
        },
      })
        .then(({ data }) => setSugs((prev) => ({ ...prev, [sid]: { loading: false, list: Array.isArray(data?.suggestions) ? data.suggestions : [] } })))
        .catch(() => setSugs((prev) => ({ ...prev, [sid]: { loading: false, list: [] } })));
    }
  };

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // V1.74 · La configuración común se aplica a todos los alumnos del planning.
  const updateCommon = (patch) => {
    setCommon((prev) => ({ ...prev, ...patch }));
    setItems((prev) => prev.map((it) => ({ ...it, ...patch })));
  };

  const setWindow = (idx, patch) =>
    setTimeWindows((prev) => prev.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  const addWindow = () => setTimeWindows((prev) => [...prev, { start: "", end: "" }]);
  const removeWindow = (idx) => setTimeWindows((prev) => prev.filter((_, i) => i !== idx));
  const validWindows = useMemo(
    () => timeWindows.filter((w) => w.start && w.end && toMinutes(w.end) > toMinutes(w.start)),
    [timeWindows],
  );

  // Pista del reparto: se pide al backend para no duplicar la lógica del programa.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const it of items) {
        try {
          const { data } = await api.get("/kai-planner/session-plan", {
            params: {
              student_id: it.student_id,
              classes_to_plan: it.classes_to_plan,
              classes_per_session: it.classes_per_session,
            },
          });
          if (!cancelled) setSplitHints((p) => ({ ...p, [it.student_id]: data }));
        } catch {
          if (!cancelled) setSplitHints((p) => ({ ...p, [it.student_id]: null }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [items]);


  const validate = async () => {
    setValidating(true);
    try {
      const windows = validWindows.length ? validWindows : DEFAULT_WINDOWS;
      api.put("/kai-planner/time-windows", { windows }).catch(() => {});
      const { data } = await api.post("/kai-planner/validate", { items, time_windows: windows });
      setReports(data.reports || []);
      setStep("review");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setValidating(false); }
  };

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const windows = validWindows.length ? validWindows : DEFAULT_WINDOWS;
      const { data } = await api.post("/kai-planner/propose", { items, teacher, time_windows: windows });
      setPlans(data.plans || []);
      setNotices(Array.isArray(data.notices) ? data.notices : []);
      setStep("result");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setGenerating(false); }
  }, [items, teacher, validWindows]);

  const actOnDraft = async (plan, draft, action) => {
    if (action === "accept" && !window.confirm(`¿Crear la cita real del ${draft.date} a las ${draft.time}?`)) return;
    setActing(true);
    try {
      await api.post(`/planner/drafts/${draft.id}/${action}`);
      setPlans((prev) => prev.map((p) => p.student_id !== plan.student_id ? p : {
        ...p,
        proposals: p.proposals.map((d) => d.id === draft.id
          ? { ...d, status: action === "accept" ? "accepted" : "rejected" } : d),
      }));
      if (action === "accept") {
        toast.success("Cita creada desde la propuesta de Kai.");
        onChanged?.();
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setActing(false); }
  };

  const saveDraftEdit = async () => {
    if (!editDraft) return;
    setActing(true);
    try {
      const { data } = await api.patch(`/kai-planner/drafts/${editDraft.id}`, {
        date: editDraft.date, time: editDraft.time,
        duration_minutes: Number(editDraft.duration_minutes),
        teach_kai: !!editDraft.teach_kai,
        teach_reason: editDraft.teach_reason || "",
      });
      setPlans((prev) => prev.map((p) => ({
        ...p,
        proposals: p.proposals.map((d) => (d.id === editDraft.id ? { ...d, ...data } : d)),
      })));
      setEditDraft(null);
      toast.success(editDraft.teach_kai
        ? "Propuesta modificada y Kai ha aprendido el cambio para este alumno."
        : "Propuesta modificada — recuerda aceptarla para crear la cita.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setActing(false); }
  };

  const totalWarnings = useMemo(
    () => reports.reduce((n, r) => n + (r.warnings || []).filter((w) => w.level === "warning").length, 0),
    [reports],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="kai-planner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-500" /> Kai Planificador
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-500">
            Planificación multi-alumno con validaciones. <b>Kai propone, tú decides.</b>
          </DialogDescription>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <StudentPicker
                  value={pickerValue}
                  onChange={setPickerValue}
                  students={available}
                  placeholder="Selecciona un alumno…"
                  testId="kai-student-picker"
                  renderMeta={(s) => (s.dni || s.phone ? String(s.dni || s.phone) : "")}
                  triggerClassName="h-10 rounded-xl"
                />
              </div>
              <button type="button" onClick={addStudent} disabled={!pickerValue}
                className="btn-primary !h-10 disabled:opacity-50" data-testid="kai-add-student">
                <Plus className="h-4 w-4" /> Añadir alumno
              </button>
            </div>

            {items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center text-sm text-neutral-500" data-testid="kai-empty-list">
                Añade del desplegable todos los alumnos a los que vas a dar clase.
                Cuando esté la lista completa, pasas a gestionar el planning.
              </div>
            ) : (
              <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden" data-testid="kai-selected-list">
                <div className="border-b border-neutral-100 bg-neutral-50 px-3 py-2 text-xs font-bold text-neutral-700">
                  Alumnos de este planning ({items.length})
                </div>
                <ol className="divide-y divide-neutral-100">
                  {items.map((it, idx) => {
                    return (
                      <li key={it.student_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`kai-selected-${idx}`}>
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold">{idx + 1}</span>
                        <span className="font-semibold">{labelOf(it.student_id)}</span>
                        <button type="button" onClick={() => removeItem(idx)}
                          className="ml-auto text-neutral-400 hover:text-red-600" data-testid={`kai-selected-remove-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            <div className="flex justify-end">
              <button type="button" onClick={() => setStep("config")} disabled={items.length === 0}
                className="btn-primary !h-10 disabled:opacity-50" data-testid="kai-go-config-btn">
                Gestionar el planning ({items.length})
              </button>
            </div>
          </div>
        )}

        {step === "config" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => setStep("select")} className="btn-secondary !h-9" data-testid="kai-back-to-list-btn">
                <ArrowLeft className="h-4 w-4" /> Alumnos ({items.length})
              </button>
              <span className="text-[11px] text-neutral-500">
                La configuración de arriba se aplica a todos; abajo puedes ajustar cada alumno.
              </span>
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-white p-3 space-y-3" data-testid="kai-common-panel">
              <div className="text-xs font-bold text-neutral-700">Configuración del planning (todos los alumnos)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs">
                  <span className="block text-neutral-500 font-semibold mb-1">Clases a programar</span>
                  <input type="number" min={1} max={30} value={common.classes_to_plan}
                    onChange={(e) => updateCommon({ classes_to_plan: Math.max(1, Math.min(30, Number(e.target.value) || 1)) })}
                    className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm"
                    data-testid="kai-common-classes" />
                </label>
                <label className="text-xs">
                  <span className="block text-neutral-500 font-semibold mb-1">Clases por sesión (preferencia)</span>
                  <select value={common.classes_per_session} onChange={(e) => updateCommon({ classes_per_session: Number(e.target.value) })}
                    className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm" data-testid="kai-common-cps">
                    {CPS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </label>
                <label className="text-xs">
                  <span className="block text-neutral-500 font-semibold mb-1">Rango de fechas · Desde</span>
                  <input type="date" value={common.plan_start_date}
                    onChange={(e) => updateCommon({ plan_start_date: e.target.value })}
                    className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm"
                    data-testid="kai-common-range-start" />
                </label>
                <label className="text-xs">
                  <span className="block text-neutral-500 font-semibold mb-1">Rango de fechas · Hasta</span>
                  <input type="date" value={common.plan_end_date} min={common.plan_start_date || undefined}
                    onChange={(e) => updateCommon({ plan_end_date: e.target.value })}
                    className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm"
                    data-testid="kai-common-range-end" />
                </label>
              </div>
              {common.plan_start_date && common.plan_end_date && common.plan_end_date < common.plan_start_date && (
                <div className="text-[11px] font-semibold text-red-600" data-testid="kai-common-range-error">
                  La fecha "Hasta" no puede ser anterior a la fecha "Desde".
                </div>
              )}

              <div className="rounded-xl border border-orange-200/70 bg-orange-50/40 p-2 space-y-2" data-testid="kai-time-windows">
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-neutral-700">
                  <Clock className="h-3.5 w-3.5 text-orange-500" /> Franjas horarias de trabajo
                  <span className="text-[10px] font-normal text-neutral-500">(Kai solo planificará dentro de ellas)</span>
                </div>
                {timeWindows.map((w, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`kai-window-${i}`}>
                    <input type="time" value={w.start} onChange={(e) => setWindow(i, { start: e.target.value })}
                      className="h-9 rounded-lg border border-neutral-200 px-2 text-sm" data-testid={`kai-window-start-${i}`} />
                    <span className="text-xs text-neutral-400">a</span>
                    <input type="time" value={w.end} onChange={(e) => setWindow(i, { end: e.target.value })}
                      className="h-9 rounded-lg border border-neutral-200 px-2 text-sm" data-testid={`kai-window-end-${i}`} />
                    <button type="button" onClick={() => removeWindow(i)}
                      className="text-neutral-400 hover:text-red-600" data-testid={`kai-window-remove-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={addWindow} className="btn-secondary !h-7 !px-2.5 !text-[11px]" data-testid="kai-window-add">
                    <Plus className="h-3 w-3" /> Añadir franja
                  </button>
                  <button type="button" onClick={() => setTimeWindows(DEFAULT_WINDOWS)}
                    className="btn-secondary !h-7 !px-2.5 !text-[11px]" data-testid="kai-window-defaults">
                    Franjas por defecto (09:00-12:00 · 16:30-22:00)
                  </button>
                  {validWindows.length === 0 && (
                    <span className="text-[11px] font-semibold text-amber-700" data-testid="kai-window-warning">
                      Sin franjas válidas: Kai usará 09:00-12:00 y 16:30-22:00.
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-700 cursor-pointer">
                  <input type="checkbox" checked={common.weekend_enabled}
                    onChange={(e) => updateCommon({ weekend_enabled: e.target.checked, weekend_days: e.target.checked ? common.weekend_days : [] })}
                    className="h-4 w-4 accent-orange-500" data-testid="kai-common-weekend" />
                  Fin de semana
                </label>
                {common.weekend_enabled && (
                  <div className="inline-flex rounded-lg border border-neutral-200 p-0.5 gap-0.5">
                    {[["saturday", "Sábado"], ["sunday", "Domingo"]].map(([v, label]) => {
                      const on = common.weekend_days.includes(v);
                      return (
                        <button key={v} type="button"
                          onClick={() => updateCommon({ weekend_days: on ? common.weekend_days.filter((d) => d !== v) : [...common.weekend_days, v] })}
                          className={"h-7 px-3 rounded-md text-xs font-bold transition-colors " + (on ? "bg-orange-500 text-white" : "text-neutral-600 hover:bg-neutral-100")}
                          data-testid={`kai-common-weekend-${v}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {items.map((it, idx) => {
                const ctx = contexts[it.student_id];
                const exam = ctx?.next_exam;
                const isOpen = !!expanded[it.student_id];
                return (
                  <div key={it.student_id} className="rounded-2xl border border-neutral-200 bg-white p-3 space-y-2.5" data-testid={`kai-item-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold">{idx + 1}</span>
                      <span className="font-semibold text-sm">{labelOf(it.student_id)}</span>
                      <button type="button" onClick={() => toggleFicha(it.student_id, it)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:text-orange-800 underline underline-offset-2"
                        data-testid={`kai-ficha-toggle-${idx}`}>
                        {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {isOpen ? "Ocultar ficha" : "Ver ficha"}
                      </button>
                      <button type="button" onClick={() => removeItem(idx)}
                        className="ml-auto text-neutral-400 hover:text-red-600" data-testid={`kai-remove-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="text-xs">
                        <span className="block text-neutral-500 font-semibold mb-1">Clases a programar</span>
                        <input type="number" min={1} max={30} value={it.classes_to_plan}
                          onChange={(e) => updateItem(idx, { classes_to_plan: Math.max(1, Math.min(30, Number(e.target.value) || 1)) })}
                          className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm"
                          data-testid={`kai-classes-${idx}`} />
                      </label>
                      <label className="text-xs">
                        <span className="block text-neutral-500 font-semibold mb-1">Clases por sesión (preferencia)</span>
                        <select value={it.classes_per_session} onChange={(e) => updateItem(idx, { classes_per_session: Number(e.target.value) })}
                          className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm" data-testid={`kai-cps-${idx}`}>
                          {CPS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                      </label>
                    </div>
                    <div className="min-h-[20px] text-[11px] text-neutral-500 flex items-start gap-1.5" data-testid={`kai-split-hint-${idx}`}>
                      <Info className="h-3.5 w-3.5 text-orange-400 shrink-0 mt-0.5" />
                      <span>
                        {(() => {
                          const p = splitHints[it.student_id];
                          if (!p) return "Calculando reparto del programa…";
                          const label = p.program_type === "repetidor" ? "repetidor"
                            : p.program_type === "programa_30" ? "programa 30" : "recorrido completo";
                          return p.manual_override
                            ? `Excepción manual: ${p.session_plan.join(" + ")} clases por sesión (${p.sessions} sesiones).`
                            : `Programa ${label}: ${p.session_plan.join(" + ")} clases por sesión (${p.sessions} sesiones).`;
                        })()}
                      </span>
                    </div>
                    <div className="text-[11px] text-neutral-500 flex items-start gap-1.5" data-testid={`kai-window-hint-${idx}`}>
                      <CalendarClock className="h-3.5 w-3.5 text-orange-400 shrink-0 mt-0.5" />
                      <span>
                        {it.plan_start_date && it.plan_end_date && it.plan_end_date >= it.plan_start_date
                          ? `Kai colocará todas las clases entre el ${formatDateES(it.plan_start_date)} y el ${formatDateES(it.plan_end_date)}.`
                          : it.tentative_exam_date
                            ? `Sin rango fijado: Kai reparte desde hoy hasta la víspera del examen (${it.tentative_exam_date}).`
                            : "Sin rango fijado: Kai reserva 3 días por sesión (mínimo 3 semanas). Puedes fijar el rango en la configuración del planning."}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="text-xs">
                        <span className="block text-neutral-500 font-semibold mb-1">Punto de recogida</span>
                        <select value={it.pickup_location_id}
                          onChange={(e) => (e.target.value === QUICK_ADD_VALUE
                            ? setQuickAdd({ idx, field: "pickup_location_id" })
                            : updateItem(idx, { pickup_location_id: e.target.value }))}
                          className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm"
                          data-testid={`kai-pickup-${idx}`}>
                          <option value="">Domicilio / automático (Kai decide)</option>
                          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                          <option value={QUICK_ADD_VALUE}>+ Añadir ubicación nueva…</option>
                        </select>
                      </label>
                      <label className="text-xs">
                        <span className="block text-neutral-500 font-semibold mb-1">Punto fin</span>
                        <select value={it.dropoff_location_id}
                          onChange={(e) => (e.target.value === QUICK_ADD_VALUE
                            ? setQuickAdd({ idx, field: "dropoff_location_id" })
                            : updateItem(idx, { dropoff_location_id: e.target.value }))}
                          className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm"
                          data-testid={`kai-dropoff-${idx}`}>
                          <option value="">Automático (Kai encadena la ruta)</option>
                          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                          <option value={QUICK_ADD_VALUE}>+ Añadir ubicación nueva…</option>
                        </select>
                      </label>
                    </div>
                    {exam ? (
                      <div className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-semibold text-violet-800" data-testid={`kai-exam-${idx}`}>
                        <GraduationCap className="h-3.5 w-3.5" />
                        {exam.type === "examen_teorico" ? "Examen teórico" : "Examen práctico"}: {formatDateES(exam.date)}
                        {exam.time ? ` · ${exam.time}` : ""}{exam.centro ? ` · ${exam.centro}` : ""}
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 text-xs">
                        <span className="text-neutral-500 font-semibold whitespace-nowrap">Fecha tentativa de examen</span>
                        <input type="date" value={it.tentative_exam_date}
                          onChange={(e) => updateItem(idx, { tentative_exam_date: e.target.value })}
                          className="h-8 rounded-lg border border-neutral-200 px-2 text-xs"
                          data-testid={`kai-tentative-exam-${idx}`} />
                        <span className="text-[10px] text-neutral-400">(opcional — sin examen DGT asignado)</span>
                      </label>
                    )}
                    <div className="rounded-xl border border-amber-200/70 bg-amber-50/40 p-2 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-[11px] font-bold text-neutral-700">
                        <NotebookPen className="h-3.5 w-3.5 text-orange-500" /> Notas de Kai sobre este alumno
                        {studentNotes[it.student_id]?.dirty && <span className="text-[10px] text-amber-700">(sin guardar)</span>}
                      </div>
                      <textarea
                        value={studentNotes[it.student_id]?.text || ""}
                        onChange={(e) => setNoteText(it.student_id, e.target.value)}
                        rows={2}
                        placeholder="Ej.: Recogerle siempre en Moncloa, no en su domicilio. Le cuesta el aparcamiento en línea…"
                        className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                        data-testid={`kai-student-note-${idx}`}
                      />
                      <button type="button" onClick={() => saveStudentNote(it.student_id)}
                        disabled={studentNotes[it.student_id]?.saving || !studentNotes[it.student_id]?.dirty}
                        className="btn-secondary !h-7 !px-2.5 !text-[11px] disabled:opacity-50"
                        data-testid={`kai-save-student-note-${idx}`}>
                        {studentNotes[it.student_id]?.saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Guardar nota
                      </button>
                    </div>
                    {isOpen && (
                      <div className="pt-1" data-testid={`kai-ficha-${idx}`}>
                        <AppointmentContextSidebar
                          context={ctx}
                          loading={!ctx}
                          suggestions={sugs[it.student_id]?.list || []}
                          suggestLoading={!!sugs[it.student_id]?.loading}
                          onApplySuggestion={() => toast.info("Sugerencia informativa — Kai la tendrá en cuenta al planificar.")}
                          teacher={ctx?.profesor_habitual || ""}
                          duration={(it.classes_per_session || 2) * 45}
                          hasStudent
                          compact
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3 text-[11px] text-neutral-600 flex items-start gap-2">
              <Info className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
              <span>
                Las <b>normas generales numeradas</b> (constitución del planificador) se editan en
                Agentes IA → pestaña <b>Kai Planificador</b>. Aquí solo se anotan las notas
                individuales de cada alumno.
              </span>
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={validate} disabled={items.length === 0 || validating}
                className="btn-primary !h-10 disabled:opacity-50" data-testid="kai-validate-btn">
                {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Validar plan
              </button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <div className={"rounded-xl border p-3 text-sm font-semibold " +
              (totalWarnings > 0 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-800")}
              data-testid="kai-validation-summary">
              {totalWarnings > 0
                ? `Kai ha detectado ${totalWarnings} aviso${totalWarnings !== 1 ? "s" : ""} que requieren tu validación:`
                : "Todo correcto. Sin avisos — puedes generar las propuestas."}
            </div>

            <div className="space-y-3">
              {reports.map((r) => (
                <div key={r.student_id} className="rounded-2xl border border-neutral-200 bg-white overflow-hidden" data-testid={`kai-report-${r.student_id}`}>
                  <div className="border-b border-neutral-100 bg-neutral-50 px-3 py-2 flex items-center gap-2 flex-wrap">
                    <User className="h-4 w-4 text-neutral-500" />
                    <span className="font-semibold text-sm">{r.student_name}</span>
                    {r.stats && (
                      <span className="ml-auto text-[10px] font-mono text-neutral-500">
                        Hechas: {r.stats.done} · Restantes bono: {r.stats.remaining}
                      </span>
                    )}
                  </div>
                  <ol className="divide-y divide-neutral-100">
                    {(r.warnings || []).map((w, i) => (
                      <li key={i} className="px-3 py-2 flex items-start gap-2 text-xs">
                        {w.level === "warning"
                          ? <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                          : <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />}
                        <span className={w.level === "warning" ? "text-amber-900" : "text-neutral-600"}>
                          <b>{i + 1}.</b> {w.message}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>

            {generating ? (
              <div className="py-6 text-center text-sm text-neutral-600" data-testid="kai-generating">
                <Loader2 className="inline h-5 w-5 animate-spin mr-2 text-orange-500" />
                Kai está planificando las sesiones…
              </div>
            ) : (
              <div className="flex justify-between gap-2 flex-wrap">
                <button type="button" onClick={() => setStep("config")} className="btn-secondary !h-10" data-testid="kai-back-btn">
                  <ArrowLeft className="h-4 w-4" /> Volver a editar
                </button>
                <button type="button" onClick={generate} className="btn-primary !h-10" data-testid="kai-generate-btn">
                  <Sparkles className="h-4 w-4" />
                  {totalWarnings > 0 ? "Validar de todas formas y generar" : "Generar propuestas"}
                </button>
              </div>
            )}
          </div>
        )}

        {step === "result" && (
          <div className="space-y-4">
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
              Propuestas generadas por Kai. Acepta las que quieras convertir en citas reales — también quedan disponibles en la pestaña <b>Kai Planificador</b>.
            </div>
            {notices.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-1.5" data-testid="kai-notices">
                <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
                  <AlertTriangle className="h-4 w-4" /> Recordatorio de Kai
                </div>
                <ol className="space-y-1">
                  {notices.map((n, i) => (
                    <li key={i} className="text-[11px] text-amber-900" data-testid={`kai-notice-${i}`}>
                      <b>{String.fromCharCode(97 + i)}.</b> {n}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {plans.map((plan) => (
              <div key={plan.student_id} className="rounded-2xl border border-neutral-200 bg-white overflow-hidden" data-testid={`kai-plan-${plan.student_id}`}>
                <div className="border-b border-neutral-100 bg-neutral-50 px-3 py-2 flex items-center gap-2 flex-wrap">
                  <User className="h-4 w-4 text-neutral-500" />
                  <span className="font-semibold text-sm">{plan.student_name}</span>
                  {plan.requested_classes != null && (
                    <span
                      className={"inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold " +
                        (plan.planned_classes === plan.requested_classes
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-900")}
                      data-testid={`kai-plan-count-${plan.student_id}`}>
                      {plan.planned_classes}/{plan.requested_classes} clases
                    </span>
                  )}
                  {plan.window_days != null && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold text-neutral-700"
                      data-testid={`kai-plan-window-${plan.student_id}`}>
                      <CalendarClock className="h-3 w-3" />
                      {plan.window_start && plan.window_end
                        ? `${formatDateES(plan.window_start)} → ${formatDateES(plan.window_end)} · ${plan.window_reason}`
                        : `${plan.window_days} días · ${plan.window_reason}`}
                    </span>
                  )}
                  {Array.isArray(plan.session_plan) && plan.session_plan.length > 0 && (
                    <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-800"
                      data-testid={`kai-plan-pattern-${plan.student_id}`}>
                      Sesiones: {plan.session_plan.join(" + ")}
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-neutral-500">{plan.proposals.length} sesión{plan.proposals.length !== 1 ? "es" : ""}</span>
                </div>
                <div className="divide-y divide-neutral-100">
                  {plan.proposals.length === 0 && (
                    <div className="px-3 py-3 text-xs text-neutral-500">Kai no encontró huecos válidos para este alumno.</div>
                  )}
                  {plan.proposals.map((d) => (
                    <div key={d.id} className="px-3 py-2.5" data-testid={`kai-draft-${d.id}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <ScoreBadge score={d.score} />
                        {d.distance_km != null && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-bold px-2 py-0.5">
                            <Route className="h-3 w-3" /> {d.distance_km} km
                          </span>
                        )}
                        <div className="ml-auto flex gap-1">
                          {d.status === "pending" ? (
                            <>
                              <button type="button"
                                onClick={() => setEditDraft(editDraft?.id === d.id ? null : { id: d.id, date: d.date, time: d.time, duration_minutes: d.duration_minutes, teach_kai: true, teach_reason: "" })}
                                disabled={acting}
                                className="inline-flex items-center gap-1 h-7 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-100 text-[11px] font-bold px-2.5 disabled:opacity-50"
                                data-testid={`kai-edit-${d.id}`}>
                                <Pencil className="h-3 w-3" /> Modificar
                              </button>
                              <button type="button" onClick={() => actOnDraft(plan, d, "accept")} disabled={acting}
                                className="inline-flex items-center gap-1 h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold px-2.5 disabled:opacity-50"
                                data-testid={`kai-accept-${d.id}`}>
                                <CheckCircle2 className="h-3 w-3" /> Aceptar
                              </button>
                              <button type="button" onClick={() => actOnDraft(plan, d, "reject")} disabled={acting}
                                className="inline-flex items-center gap-1 h-7 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-[11px] font-bold px-2.5 disabled:opacity-50"
                                data-testid={`kai-reject-${d.id}`}>
                                <X className="h-3 w-3" /> Rechazar
                              </button>
                            </>
                          ) : (
                            <span className={"inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold border " +
                              (d.status === "accepted" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800")}>
                              {d.status === "accepted" ? "Aceptada" : "Rechazada"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-xs">
                        <div className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-neutral-400" /><b>{d.date}</b> · <span className="font-mono">{d.time}–{addMinutes(d.time, Number(d.duration_minutes || 45))}</span> <span className="text-orange-600 font-semibold">({Math.max(1, Math.round(Number(d.duration_minutes || 45) / 45))} clases)</span></div>
                        <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-neutral-400" />{d.teacher_name || "—"}</div>
                        {d.zone && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-neutral-400" />{d.zone}</div>}
                      </div>
                      {(d.pickup_location_name || d.dropoff_location_name) && (
                        <div className="mt-1 text-[11px] text-neutral-600 flex items-center gap-1.5">
                          <Route className="h-3 w-3 text-neutral-400" />
                          {d.pickup_location_name || "Domicilio"} → {d.dropoff_location_name || "automático"}
                        </div>
                      )}
                      {editDraft?.id === d.id && (
                        <div className="mt-2 rounded-xl border border-orange-200 bg-orange-50/50 p-2 flex flex-wrap items-end gap-2" data-testid={`kai-edit-form-${d.id}`}>
                          <label className="text-[10px] font-semibold text-neutral-600">
                            Fecha
                            <input type="date" value={editDraft.date}
                              onChange={(e) => setEditDraft({ ...editDraft, date: e.target.value })}
                              className="block h-8 rounded-lg border border-neutral-200 px-2 text-xs bg-white"
                              data-testid={`kai-edit-date-${d.id}`} />
                          </label>
                          <label className="text-[10px] font-semibold text-neutral-600">
                            Inicio
                            <input type="time" value={editDraft.time}
                              onChange={(e) => setEditDraft({ ...editDraft, time: e.target.value })}
                              className="block h-8 rounded-lg border border-neutral-200 px-2 text-xs bg-white font-mono"
                              data-testid={`kai-edit-time-${d.id}`} />
                          </label>
                          <label className="text-[10px] font-semibold text-neutral-600">
                            Clases
                            <input type="number" min={1} max={6}
                              value={Math.max(1, Math.round(Number(editDraft.duration_minutes || 45) / 45))}
                              onChange={(e) => {
                                const n = Math.min(6, Math.max(1, Number(e.target.value) || 1));
                                setEditDraft({ ...editDraft, duration_minutes: n * 45 });
                              }}
                              className="block h-8 w-16 rounded-lg border border-neutral-200 px-2 text-xs bg-white font-mono text-center"
                              data-testid={`kai-edit-classes-${d.id}`} />
                          </label>
                          <label className="text-[10px] font-semibold text-neutral-600">
                            Fin
                            <input type="time"
                              value={addMinutes(editDraft.time, Number(editDraft.duration_minutes || 45))}
                              onChange={(e) => {
                                const mins = toMinutes(e.target.value) - toMinutes(editDraft.time);
                                if (mins > 0) setEditDraft({ ...editDraft, duration_minutes: mins });
                                else toast.error("La hora de fin debe ser posterior a la de inicio");
                              }}
                              className="block h-8 rounded-lg border border-neutral-200 px-2 text-xs bg-white font-mono"
                              data-testid={`kai-edit-end-${d.id}`} />
                          </label>
                          <span className="text-[10px] text-neutral-500 pb-1.5" data-testid={`kai-edit-duration-hint-${d.id}`}>
                            {Number(editDraft.duration_minutes || 45)} min
                          </span>
                          <div className="w-full flex flex-wrap items-center gap-2 pt-1 border-t border-orange-100">
                            <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-neutral-700 cursor-pointer">
                              <input type="checkbox" checked={!!editDraft.teach_kai}
                                onChange={(e) => setEditDraft({ ...editDraft, teach_kai: e.target.checked })}
                                className="h-3.5 w-3.5 accent-orange-500"
                                data-testid={`kai-edit-teach-${d.id}`} />
                              Enseñar a Kai este cambio
                            </label>
                            {editDraft.teach_kai && (
                              <input type="text" value={editDraft.teach_reason || ""}
                                onChange={(e) => setEditDraft({ ...editDraft, teach_reason: e.target.value })}
                                placeholder="Motivo (opcional): ej. el profesor no trabaja a esa hora…"
                                className="flex-1 min-w-[180px] h-8 rounded-lg border border-neutral-200 px-2 text-xs bg-white"
                                data-testid={`kai-edit-reason-${d.id}`} />
                            )}
                          </div>
                          <div className="flex gap-1 ml-auto">
                            <button type="button" onClick={saveDraftEdit} disabled={acting}
                              className="inline-flex items-center gap-1 h-8 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-bold px-2.5 disabled:opacity-50"
                              data-testid={`kai-edit-save-${d.id}`}>
                              <CheckCircle2 className="h-3 w-3" /> {editDraft.teach_kai ? "Guardar y enseñar a Kai" : "Guardar cambios"}
                            </button>
                            <button type="button" onClick={() => setEditDraft(null)}
                              className="h-8 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-100 text-[11px] font-bold px-2.5"
                              data-testid={`kai-edit-cancel-${d.id}`}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}
                      {d.reason && <div className="mt-1 text-[11px] text-neutral-600 border-l-2 border-orange-200 pl-2 italic">{d.reason}</div>}
                      {(d.alerts || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {d.alerts.map((a, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5">
                              <AlertTriangle className="h-3 w-3" /> {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-between gap-2 flex-wrap">
              <button type="button"
                onClick={() => { setItems([]); setReports([]); setPlans([]); setNotices([]); setStep("select"); }}
                className="btn-secondary !h-10" data-testid="kai-plan-another-btn">
                <Plus className="h-4 w-4" /> Planificar otros alumnos
              </button>
              <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary !h-10" data-testid="kai-close-btn">
                Cerrar
              </button>
            </div>
          </div>
        )}
        <QuickAddDialog
          open={!!quickAdd}
          entity="location"
          onClose={() => setQuickAdd(null)}
          onCreated={async (doc) => {
            const target = quickAdd;
            setQuickAdd(null);
            const { data } = await api.get("/locations");
            setLocations(Array.isArray(data) ? data : []);
            if (target && doc?.id) updateItem(target.idx, { [target.field]: doc.id });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
